import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, Loader2, Lock, Mail, X } from 'lucide-react';
import './AddProductModal.css';

export interface ReauthModalProps {
  isOpen: boolean;
  userEmail: string;
  existingProductName: string;
  newProductName: string;
  jiraKey: string;
  loading: boolean;
  error?: string | null;
  onConfirm: (password: string) => void;
  onCancel: () => void;
}

/**
 * Re-authentication modal used by the hard-delete-and-recreate flow.
 * Mirrors the /auth page UX (email + password, Mail/Lock icons, show/hide toggle)
 * so the confirm step matches the login experience. The email is fixed to the
 * signed-in account — only the password is user-supplied — and is verified
 * server-side via Supabase `signInWithPassword`.
 */
export function ReauthModal({
  isOpen,
  userEmail,
  existingProductName,
  newProductName,
  jiraKey,
  loading,
  error,
  onConfirm,
  onCancel,
}: ReauthModalProps) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setShowPassword(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;
  if (typeof document === 'undefined' || !document.body) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    onConfirm(password);
  };

  const emailDisplay = userEmail.trim() || '—';

  return createPortal(
    <div
      className="add-product-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reauth-modal-title"
      style={{ zIndex: 'calc(var(--z-modal, 1000) + 240)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onCancel();
      }}
    >
      <div className="add-product-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="add-product-modal-header">
          <h2 id="reauth-modal-title">Confirm with your password</h2>
          <button
            type="button"
            className="add-product-modal-close"
            onClick={onCancel}
            aria-label="Close"
            disabled={loading}
          >
            <X size={20} aria-hidden />
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="add-product-modal-body">
            <p
              className="add-product-modal-error-text"
              style={{ color: 'var(--color-text, #0f172a)' }}
            >
              Permanently delete <strong>{existingProductName}</strong> and create{' '}
              <strong>{newProductName}</strong> with Jira key <strong>{jiraKey}</strong>. This
              cannot be undone.
            </p>
            <p className="form-hint" style={{ marginTop: '0.5rem' }}>
              Enter your account password to confirm. Verification uses the same Supabase sign-in as
              the login page.
            </p>

            {error ? (
              <div
                className="add-product-modal-error"
                role="alert"
                style={{ marginTop: '0.75rem' }}
              >
                <p className="add-product-modal-error-text">{error}</p>
              </div>
            ) : null}

            <div className="form-group">
              <label htmlFor="reauth-email" className="form-label">
                Email
              </label>
              <div className="add-product-modal-input-wrapper">
                <Mail className="add-product-modal-input-icon" size={16} aria-hidden />
                <input
                  id="reauth-email"
                  type="email"
                  className="form-input add-product-modal-input-with-icon"
                  value={emailDisplay}
                  disabled
                  readOnly
                  autoComplete="username"
                  title={userEmail.trim() ? userEmail : 'Email not available in the UI'}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="reauth-password" className="form-label">
                Password
              </label>
              <div className="add-product-modal-input-wrapper">
                <Lock className="add-product-modal-input-icon" size={16} aria-hidden />
                <input
                  id="reauth-password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input add-product-modal-input-with-icon add-product-modal-input-with-trailing"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  autoFocus
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  className="add-product-modal-input-trailing"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
                </button>
              </div>
            </div>
          </div>
          <div className="add-product-modal-footer">
            <button
              type="button"
              className="add-product-modal-btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="add-product-modal-btn-danger"
              disabled={loading || password.length === 0}
            >
              {loading ? <Loader2 className="add-product-modal-spin" size={18} /> : null}
              {loading ? 'Working…' : 'Confirm hard delete and recreate'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
